import { useEffect, useState } from 'react';
import {
  fetchTags,
  getTagLoadingSubject,
  getTagSubject,
  handleTagChange,
  SupportedTagEntities,
  SupportedTagEntityTypes,
  Tag
} from '../../../store/Tags';
// Not store/User: that module reaches this component through four grids' column definitions.
import { getCanEditTags, getCanEditTagsSubject } from '../../../store/tagEditPermission';
import { TagsInput } from './TagsInput';
import { TagsDetails } from './TagsDetails';
import { Icon } from '../../Atoms/Icon';
import { SrOnlyText } from '../../Atoms/Text';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { sameAccount } from '../../../utils/accountIds';

// `gone` = refresh refused, so the record left the caller's reach; `void` because most callers report nothing.
export type TagRefreshOutcome = 'gone' | void;
export type TagEntityRefresh = (id: string) => Promise<TagRefreshOutcome>;

export interface TagsProps {
  entity: SupportedTagEntities;
  entityType: SupportedTagEntityTypes;
  entityFetchFn: TagEntityRefresh;
  // The id tag relations key on; callers resolve it because a row's own `id` is sometimes the Salesforce one.
  entityId: string;
  // The record's own account: the api refuses a tag from any other one, so unset means the caller could not name it.
  entityAccountId?: string;
  // Set where the caller already knows a write cannot land, e.g. a grid row whose id never resolved.
  readOnly?: boolean;
  showCTAButton?: boolean;
  isGrid?: boolean;
}

export const Tags = ({
  entity,
  entityType,
  entityFetchFn,
  entityId,
  entityAccountId,
  readOnly = false,
  showCTAButton = false,
  isGrid = false
}: TagsProps) => {
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [tagsLoading, setTagsLoading] = useState<boolean>(false);
  const [updatesLoading, setUpdatesLoading] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState(false);
  const { setSnack } = useSnackbar();
  const [canEditTags, setCanEditTags] = useState(getCanEditTags());
  const entityTags = entity.tags || [];
  const canEdit = canEditTags && !readOnly;
  const editing = isEditing && canEdit;
  // Offering another account's tag only buys the user a 422; already-attached ones stay on the chip row regardless.
  const offeredTags = entityAccountId
    ? availableTags.filter((tag) => sameAccount(tag.accountId, entityAccountId))
    : availableTags;

  useEffect(() => {
    const tagSubscription = getTagSubject().subscribe((tags) => {
      setAvailableTags(tags);
    });
    const tagLoadingSubscription = getTagLoadingSubject().subscribe((loading) => setTagsLoading(loading));
    // Subscribed, not read once: a role change on a mounted cell must arm or disarm the editor without a re-render.
    const permissionSubscription = getCanEditTagsSubject().subscribe(setCanEditTags);

    fetchTags();

    return () => {
      if (tagSubscription) tagSubscription.unsubscribe();
      if (tagLoadingSubscription) tagLoadingSubscription.unsubscribe();
      permissionSubscription.unsubscribe();
    };
  }, []);

  const handleEntityTagsChange = async (tags: (Tag | string)[]) => {
    setUpdatesLoading(true);
    try {
      // Snackbar too: no page renders the tag error domain, so a failure would just look like the chip reverting.
      const { error } = await handleTagChange(tags, entityId, entityType, entityTags, entityAccountId);
      // The api names an account mismatch precisely; the flat line only stands in when the refusal carried no words.
      if (error !== undefined) setSnack({ message: error || 'Failed to update tags.', type: 'error', open: true });
      const gone = (await entityFetchFn(entityId)) === 'gone';
      // Said out loud: a silent disappearance reads as a deletion — but one snackbar holds one message.
      if (gone && error === undefined)
        setSnack({ message: 'This record is no longer visible to you.', type: 'info', open: true });
    } finally {
      // A rejected refetch would otherwise leave the row spinning with no way back.
      setUpdatesLoading(false);
    }
  };

  return (
    <div className={isGrid ? 'w-full py-1 h-full' : 'max-w-96'}>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div
        className="w-full h-full"
        onBlur={() => setIsEditing(false)}
        // Gated, not just unrendered: readOnly can flip false on a mounted cell, and a click armed while it was true would open the editor by itself.
        onClick={() => canEdit && setIsEditing(true)}>
        {editing && (
          <TagsInput
            entityId={entityId}
            entityType={entityType}
            availableTags={offeredTags}
            loading={tagsLoading || updatesLoading}
            updating={updatesLoading}
            selectedTags={entityTags}
            onTagChange={handleEntityTagsChange}
            isGrid={isGrid}
          />
        )}
        {!editing && (
          <div className={`flex items-start justify-between ${isGrid ? 'pl-2' : 'pr-2'}`}>
            <TagsDetails entityTags={entityTags} />
            {showCTAButton && canEdit && (
              <div>
                <button
                  className="flex items-center gap-x-2 space-x-1 text-grey-12 text-sm"
                  data-cy="new-tag"
                  onClick={() => setIsEditing(true)}>
                  <Icon type="plus" />
                  {entityTags.length === 0 && 'Add Tags'}
                  <SrOnlyText>Create or add a tag</SrOnlyText>
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
